// =================================================================================================
// Main App
// =================================================================================================

import '../App.scss';
import { useContext, useEffect, useState } from 'react';
import { getApiProducts } from '../apiCalls';
import { DeploymentHost, TBranding, TError, TJourneyConfig, TJourneyMode, TProductData } from '../types';
import { Journey } from '../components/Journey';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { SuccessPage } from '../components/SuccessPage';
import {
  configureAPIs,
  DEFAULT_UUID,
  getBranding,
  getProduct1,
  // getProduct,
  // getProducts,
  getQuote,
  getQuoteSummary,
} from '../apiCalls';

import { JourneyPicker } from '../components/JourneyPicker';
import { defaultBrand, defaultTheme, V2RenderVersionThemeExtras } from '../defaultBranding';
import { CenteredError } from '../components/CenteredError';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { JourneyEditApp } from '../JourneyEditApp';
import { getEnvParams } from '../utils';
import { ProductsContext } from '../ProductsApiContext';
import { allJourneyConfigs, getJourneyById } from '../journeyConfigs';
import { activeJourneyContext } from '../activeJourneyContext';
import ScratchAndDentPersonalOneEdge from '../journeyConfigs/pro-e81ce45f-6909-485f-81d7-f7935065446d/ScratchAndDentPersonalOneEdge.json';
import cedar from '../journeyConfigs/pro-dc90cf75-a248-4d57-930f-b7373b6d39db/cedar.json';
import productTester from '../journeyConfigs/no-id/Product Tester.json';
import PBUIFields from '../journeyConfigs/no-id/PBUIFieldsPreview.json';
import { GroupsFieldProvider } from '../CollapsableSectionsContext';
import SuccessPageV2 from '../components/SuccessPageV2';
import { hexToRGBA } from '../utils';
import AppliedContextProvider from '../AppliedContext';
import { useCookies } from 'react-cookie';
import { CookieBanner } from '../components/CookieBanner';
import { BrowserRouter, Route, Router, Routes } from 'react-router-dom';
import getBrokerDataFromProduct from '../utils/getBrokerDataFromProduct';
import { PAYMENT_TYPES } from '../utils/Constants';
import { useInitialDataLoader } from '../hooks/useInitialDataLoader';

console.log('Loaded on ' + new Date());

// =================================================================================================
// Main component
// =================================================================================================

const envParams = getEnvParams();

const Home = ({ fetchedBranding }: { fetchedBranding: any }) => {
  // -----------------------------------------------------------------------------------------------
  // App state
  // -----------------------------------------------------------------------------------------------

  const [journeys, setJourneys] = useState<TProductData[] | null>(null);
  const [activeJourney, setActiveJourney] = useState<TJourneyConfig | null>(null);
  const [theme, setTheme] = useState(defaultTheme);
  const [branding, setBranding] = useState<TBranding>(defaultBrand);
  const [journeyMode, setJourneyMode] = useState<TJourneyMode>('LOADING');
  const [error, setError] = useState<TError>({});
  const [productData, setProductData] = useState<TProductData | null>(null);
  const [successToken, setSuccessToken] = useState('');
  const [productsLoading, setProductsLoading] = useState(true);

  const [cookies] = useCookies(['cookieBanner']);

  const { updateProductData, product } = useContext(ProductsContext);
  const url = process.env.REACT_APP_TEMP_ENDPOINT;
  console.log(url);

  const { userSessionToken, quoteId, productId } = envParams;
  const uuid = fetchedBranding ? fetchedBranding.uuid : envParams.uuid;
  console.log('uuid', uuid);

  // -----------------------------------------------------------------------------------------------
  // useEffect for loading initial data from APIs
  // -----------------------------------------------------------------------------------------------

  
  useEffect(() => {
    document.title = activeJourney?.product?.title || 'Welcome';
  }, [activeJourney]);
  

  // -----------------------------------------------------------------------------------------------
  // Theme and branding
  // -----------------------------------------------------------------------------------------------

  const handleBranding = (branding: TBranding, renderVersion: 'V1' | 'V2' = 'V1') => {
    console.log({ renderVersion });
    console.log('Creating Theme: ', branding);
    try {
      const brandedTheme = createTheme(
        renderVersion === 'V2'
          ? {
              ...V2RenderVersionThemeExtras,
              palette: {
                primary: {
                  main: branding.main_color ?? '#3B90CF',
                },
                secondary: {
                  main: hexToRGBA(defaultBrand.main_color, 0.08),
                },
              },
            }
          : {
              palette: {
                primary: {
                  main: branding.main_color ?? '#3B90CF',
                },
              },
            },
      );
      if (branding.name) {
        document.title = branding.name;
      }
      setBranding(branding);
      setTheme(brandedTheme);
    } catch (e) {
      return false;
    }
    return true;
  };

    useInitialDataLoader({
    envParams,
    uuid,
    fetchedBranding,
    setJourneys,
    setJourneyMode,
    setError,
    setProductData,
    updateProductData,
    setActiveJourney,
    setProductsLoading,
    handleBranding,
  });

  // -----------------------------------------------------------------------------------------------
  // Main rendering
  // -----------------------------------------------------------------------------------------------

  if (journeyMode === 'EDIT') {
    return <JourneyEditApp />;
  }

  if (journeyMode === 'LOADING' && !fetchedBranding) {
    return <LoadingIndicator />;
  }

  if (journeyMode === 'ERROR') {
    return <CenteredError title={error.title} content={error.content} />;
  }

  if (journeyMode === 'SUCCESS') {
    if (activeJourney?.renderVersion === 'V2') {
      return (
        <SuccessPageV2
          config={activeJourney}
          quoteId={envParams.userSessionToken}
          branding={fetchedBranding ?? branding}
        />
      );
    } else {
      return <SuccessPage quoteId={envParams.userSessionToken} branding={fetchedBranding ?? branding} />;
    }
  }
  if (successToken) {
    if (activeJourney?.renderVersion === 'V2') {
      return <SuccessPageV2 config={activeJourney} quoteId={successToken} branding={fetchedBranding ?? branding} />;
    } else {
      return <SuccessPage quoteId={successToken} branding={fetchedBranding ?? branding} />;
    }
  }

  const findGroupFields = (fields: any[]) => {
    return fields?.flatMap((f: any) => {
      const nested: any[] = f?.fields ? findGroupFields(f?.fields) : [];
      return f?.type === 'group' ? [f, ...nested] : nested;
    });
  };

  const groupFields = activeJourney?.sections?.flatMap((s) => findGroupFields(s?.fields));

  console.log('activeJourney ==> ', activeJourney);
  return (
    <activeJourneyContext.Provider value={activeJourney}>
      <ThemeProvider theme={theme}>
        {activeJourney === null && !productId && !quoteId ? (
          <Routes>
            <Route
              path="*"
              element={
                <JourneyPicker
                  productsLoading={productsLoading}
                  branding={fetchedBranding ?? branding}
                  journeys={journeys || []}
                  setActiveJourney={setActiveJourney}
                  mode={journeyMode}
                  theme={theme}
                  setTheme={setTheme}
                />
              }
            />
          </Routes>
        ) : (
          <AppliedContextProvider>
            <GroupsFieldProvider firstFieldId={groupFields?.[0]?.name}>
              <Journey
                productsLoading={productsLoading}
                branding={fetchedBranding ?? branding}
                config={{
                  ...activeJourney,
                  product: {
                    ...activeJourney?.product,
                    title: productData?.productName ?? activeJourney?.product.title,
                  },
                  payment: {
                    provider: product
                      ? // setting payment provider based on product data in case it was set on product level but not journey config
                        getBrokerDataFromProduct(product, uuid)?.isStatementOfAccounts
                        ? PAYMENT_TYPES.SOA
                        : activeJourney?.payment?.provider
                      : activeJourney?.payment?.provider,
                  },
                }}
                setSuccessToken={setSuccessToken}
                setActiveJourney={setActiveJourney}
                mode={journeyMode}
              />
            </GroupsFieldProvider>
          </AppliedContextProvider>
        )}
        {!cookies.cookieBanner && <CookieBanner uuid={String(uuid)} />}
      </ThemeProvider>
    </activeJourneyContext.Provider>
  );
};

export default Home;
